import React, { useEffect, useState, useContext } from 'react';
import api, { BACKEND_URL } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SupplementaryForm from '../components/SupplementaryForm';
import StudentAvatar from '../components/StudentAvatar';
import { 
  FileText, 
  Search, 
  Plus, 
  X, 
  Globe, 
  User, 
  Edit3, 
  Filter,
  AlertCircle,
  Trash2
} from 'lucide-react';

const Applications = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [students, setStudents] = useState([]);
  const [activeCountries, setActiveCountries] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentAppId, setCurrentAppId] = useState(null);
  const [formData, setFormData] = useState({
    student_id: '', country: '', status: 'Processing', additional_details: {}
  });

  const fetchData = async (preserveFilter = false) => {
    try {
      setLoading(true);
      const [appRes, stuRes, countrRes] = await Promise.all([
        api.get('/applications'),
        api.get('/students'),
        api.get('/countries')
      ]);
      setApplications(appRes.data);
      setStudents(stuRes.data.filter(s => !s.visa_type || s.visa_type === 'Student'));
      const activeList = countrRes.data.filter(c => c.status === 'active');
      setActiveCountries(activeList);
      
      if (!preserveFilter) {
          setSelectedCountry('All');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { photo_base64, ...detailsToSave } = formData.additional_details || {};
      const submitData = { ...formData, additional_details: detailsToSave };
      if (isEditing) {
          await api.put(`/applications/${currentAppId}`, submitData);
          alert('Application updated successfully!');
      } else {
          await api.post('/applications', submitData);
          alert('Application created successfully!');
      }
      fetchData(true);
      resetForm();
    } catch (err) {
      alert(err.response?.data?.details?.[0] || err.response?.data?.error || 'Error processing application');
    }
  };

  const handleEdit = (app) => {
      setIsEditing(true);
      setCurrentAppId(app.id);
      setFormData({
          student_id: app.student_id,
          country: app.country,
          status: app.status || 'Applied',
          additional_details: app.additional_details || {}
      });
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

   const handleDelete = async (id) => {
       if (!window.confirm('Are you sure you want to delete this application?')) return;
       try {
           await api.delete(`/applications/${id}`);
           alert('Application deleted successfully!');
           fetchData(true);
       } catch (err) {
           alert('Error deleting application');
       }
   };

   const resetForm = () => {
      setShowForm(false);
      setIsEditing(false);
      setCurrentAppId(null);
      setFormData({ student_id: '', country: '', status: 'Processing', additional_details: {} });
  };

  const handleStudentChange = (studentId) => {
    if (!studentId) {
      setFormData(prev => ({
        ...prev,
        student_id: '',
        country: '',
        additional_details: {}
      }));
      return;
    }

    const student = students.find(s => String(s.id) === String(studentId));
    if (!student) return;

    const parts = (student.full_name || '').trim().split(' ');
    const familyName = parts.length > 1 ? parts[parts.length - 1] : '';
    const givenName = parts.length > 1 ? parts.slice(0, parts.length - 1).join(' ') : parts[0] || '';

    const newDetails = {
      family_name: familyName,
      given_name: givenName,
      tel_no_1: student.phone || '',
      current_address: student.temporary_address || student.permanent_address || '',
      father_name: student.father_name || '',
      mother_name: student.mother_name || '',
      place_of_birth: student.nationality || '',
      native_language: student.nationality?.toLowerCase() === 'nepali' || student.nationality?.toLowerCase() === 'nepalese' ? 'Nepali' : '',
    };

    if (student.photo_url) {
      newDetails.photo_base64 = student.photo_url.startsWith('http') ? student.photo_url : `${BACKEND_URL}${student.photo_url}`;
    }

    setFormData(prev => ({
      ...prev,
      student_id: studentId,
      country: student.abroad_study_country || prev.country || '',
      additional_details: {
        ...prev.additional_details,
        ...newDetails
      }
    }));
  };

  const getFilteredApps = () => {
    let filtered = applications;
    if (selectedCountry && selectedCountry !== 'All') {
        filtered = filtered.filter(app => app.country === selectedCountry);
    }
    if (searchTerm) {
        const lowerTerm = searchTerm.toLowerCase();
        filtered = filtered.filter(app => 
            (app.application_name && app.application_name.toLowerCase().includes(lowerTerm)) || 
            (app.student_name && app.student_name.toLowerCase().includes(lowerTerm)) ||
            (app.university && app.university.toLowerCase().includes(lowerTerm)) ||
            (app.passport_number && app.passport_number.toLowerCase().includes(lowerTerm)) ||
            (app.phone && app.phone.includes(lowerTerm))
        );
    }
    return filtered;
  };

  const filteredApps = getFilteredApps();

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-bold animate-pulse">Loading Applications...</p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-8 animate-in fade-in duration-500 relative h-full">
       <style dangerouslySetInnerHTML={{ __html: `
          @media print {
              .no-print { display: none !important; }
              body { background: white !important; }
              .card { box-shadow: none !important; border: 1px solid #eee !important; }
          }
      `}} />

      <div className="flex flex-col md:flex-row justify-between items-center gap-6 no-print">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Manage Applications</h1>
          <p className="text-sm text-gray-500 font-semibold">Track and process student visas across global regions.</p>
        </div>
        <button 
          className={`btn-primary !px-6 transition-all ${showForm ? '!bg-gray-500 hover:!bg-gray-600' : ''}`} 
          onClick={() => showForm ? resetForm() : setShowForm(true)}
        >
          {showForm ? <X size={18} /> : <Plus size={18} />}
          {showForm ? 'Discard Changes' : 'Add New Application'}
        </button>
      </div>

      {showForm && (
        <div className="card border-l-4 border-indigo-500 no-print animate-in slide-in-from-top-4 duration-300">
          <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
            <h2 className="text-lg font-black text-indigo-900 flex items-center gap-2">
              <FileText size={20} />
              {isEditing ? `Edit Application: ${applications.find(a => a.id === currentAppId)?.student_name}` : 'Add New Application'}
            </h2>
            <button onClick={resetForm} className="text-gray-400 hover:text-rose-500 transition-colors">
              <X size={24} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2 space-y-1.5">
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest ml-1">Select Student <span className="text-rose-500">*</span></label>
                <select 
                  className="input-field bg-white" 
                  value={formData.student_id} 
                  onChange={e => handleStudentChange(e.target.value)} 
                  required
                  disabled={isEditing}
                >
                  <option value="">Select a student from list</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.full_name}{s.passport_no ? ` (${s.passport_no})` : ''}</option>)}
                </select>
                {isEditing && <p className="text-[10px] text-indigo-400 font-bold uppercase tracking-widest mt-1 ml-1 flex items-center gap-1"><AlertCircle size={10} /> Student name cannot be changed</p>}
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest ml-1">Country <span className="text-rose-500">*</span></label>
                <select 
                  className="input-field bg-white" 
                  value={formData.country} 
                  onChange={e => setFormData({...formData, country: e.target.value})} 
                  required
                >
                  <option value="">Select country</option>
                  {activeCountries.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              {isEditing && user?.role === 'Admin' && (
                <div className="space-y-1.5">
                  <label className="block text-xs font-black text-gray-500 uppercase tracking-widest ml-1">Status <span className="text-rose-500">*</span></label>
                  <select 
                    className="input-field bg-white" 
                    value={formData.status} 
                    onChange={e => setFormData({...formData, status: e.target.value})} 
                    required
                  >
                    <option value="Processing">Processing</option>
                    <option value="Applied">Applied</option>
                    <option value="Interview">Interview</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              )}
            </div>

            {formData.country && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="py-4 border-t border-gray-100">
                      <SupplementaryForm 
                          countryName={formData.country}
                          details={formData.additional_details} 
                          onChange={(name, value) => {
                              setFormData(prev => ({
                                  ...prev,
                                  additional_details: { ...prev.additional_details, [name]: value }
                              }));
                          }} 
                      />
                  </div>
                  <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                    <button type="button" onClick={resetForm} className="px-6 py-2.5 text-gray-500 font-bold text-sm hover:bg-gray-100 rounded-xl transition-all">Cancel</button>
                    <button type="submit" className="btn-primary !px-10 shadow-indigo-200">
                        {isEditing ? 'Save Changes' : 'Save Application'}
                    </button>
                  </div>
              </div>
            )}
          </form>
        </div>
      )}

      <div className="flex flex-col gap-6 flex-1 min-h-0">
        <div className="no-print">
          <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className="flex items-center gap-2 shrink-0 px-2">
              <Filter size={14} className="text-indigo-600" />
              <h3 className="font-black text-gray-400 uppercase text-[10px] tracking-[0.2em]">Filter</h3>
            </div>
            
            <div className="flex-1 max-w-xs relative">
              <select
                value={selectedCountry}
                onChange={e => setSelectedCountry(e.target.value)}
                className="w-full pl-5 pr-10 py-3 rounded-2xl bg-gray-50 border border-gray-100 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-gray-700 transition-all appearance-none cursor-pointer"
              >
                <option value="All">All Countries ({applications.length})</option>
                {activeCountries.map(country => {
                  const count = applications.filter(a => a.country === country.name).length;
                  return (
                    <option key={country.id} value={country.name}>
                      {country.name} ({count})
                    </option>
                  );
                })}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden border-b-4 border-b-sky-500">
          <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center bg-white gap-4 no-print">
             <div>
                <h2 className="font-black text-gray-900 tracking-tight text-lg flex items-center gap-2">
                  <Globe className="text-indigo-600" size={20} />
                  {selectedCountry === 'All' ? 'All Applications' : `Applications for ${selectedCountry}`}
                </h2>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Updated automatically</p>
             </div>
             <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1">
                   <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                   <input 
                      type="text" 
                      placeholder="Search files..." 
                      className="input-field !pl-10 !py-2 !text-xs !rounded-xl !bg-gray-50 focus:!bg-white"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
             </div>
          </div>
          
          <div className="overflow-x-auto flex-1 scrollbar-hide">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-4 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[25%]">Student Name</th>
                  {selectedCountry === 'All' && <th className="px-4 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[15%]">Country</th>}
                  {user?.role === 'Admin' && <th className="px-4 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[15%]">Status</th>}
                  <th className="px-4 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-[20%]">Added By</th>
                  <th className="px-4 py-4 text-xs font-black text-gray-400 uppercase tracking-widest text-right no-print w-[15%]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredApps.length === 0 && (
                   <tr>
                    <td colSpan={selectedCountry === 'All' ? 5 : 4} className="p-20 text-center">
                       <FileText className="mx-auto text-gray-100 mb-4" size={48} />
                       <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">No matching student records found</p>
                    </td>
                   </tr>
                )}
                {filteredApps.map(app => (
                  <tr key={app.id} className="hover:bg-gray-50/50 transition-all group">
                    <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                           <StudentAvatar photoUrl={app.photo_url} fullName={app.student_name} className="w-10 h-10 rounded-xl" />
                           <div>
                              <p className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors text-sm">{app.student_name}</p>
                              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">PN: {app.passport_number || 'PENDING'}</p>
                           </div>
                        </div>
                    </td>
                    {selectedCountry === 'All' && (
                        <td className="px-4 py-4">
                            <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-2.5 py-1 rounded-md uppercase tracking-widest">{app.country}</span>
                        </td>
                    )}
                    {user?.role === 'Admin' && (
                      <td className="px-4 py-4">
                        <span className={`status-badge ${app.status === 'Approved' ? 'status-active' : app.status === 'Rejected' ? 'status-danger' : 'status-pending'}`}>
                          {app.status}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <p className="text-xs font-bold text-gray-700">{app.creator_name || 'System'}</p>
                      {app.modifier_name && app.modifier_name !== app.creator_name && (
                        <p className="text-[9px] text-indigo-500 font-bold uppercase tracking-tight">Modified by {app.modifier_name}</p>
                      )}
                      <p className="text-[10px] text-gray-400">{new Date(app.created_at).toLocaleDateString()}</p>
                    </td>
                    <td className="px-4 py-4 text-right no-print">
                         <div className="flex justify-end gap-2">
                           <button 
                               onClick={() => navigate(`/applications/${app.id}`)}
                               className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                               title="View Profile"
                           >
                               <User size={18} />
                           </button>
                           <button 
                               onClick={() => handleEdit(app)}
                               className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                               title="Edit Entry"
                           >
                               <Edit3 size={18} />
                           </button>
                           {user?.role === 'Admin' && (
                               <button 
                                   onClick={() => handleDelete(app.id)}
                                   className="p-2 text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                                   title="Delete Entry"
                               >
                                   <Trash2 size={18} />
                               </button>
                           )}
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Applications;
